import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';
import { VEHICLES } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: '', phone: '', email: '', vehicle: '', pickup_date: '',
  return_date: '', pickup_location: '', delivery_address: '', special_requests: '',
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-gold/80">{label}</span>
    {children}
  </label>
);

const inputCls =
  'w-full border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm font-light text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold/70 focus:bg-white/[0.05]';

const Reserve = ({ selectedVehicle }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (selectedVehicle) setForm((f) => ({ ...f, vehicle: selectedVehicle }));
  }, [selectedVehicle]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, form);
      setDone(true);
    } catch (err) {
      toast.error('Something went wrong. Please call or text us at 786-920-2715.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reserve" data-testid="reserve-section" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-28 lg:px-10 lg:py-36">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Reserve" title={<>Your keys are<br /><span className="italic text-gold">waiting.</span></>} />
          <Reveal delay={0.2}>
            <p className="max-w-md text-base font-light leading-relaxed text-white/55">
              Tell us when and where — a member of our concierge team will confirm availability personally, typically within the hour during business hours.
            </p>
            <div className="mt-8 space-y-3 text-sm text-white/60">
              <p><span className="text-gold">—</span> Daily, weekend &amp; long-term rentals</p>
              <p><span className="text-gold">—</span> Airport &amp; hotel delivery available</p>
              <p><span className="text-gold">—</span> Open 7 days, 8:00 AM – 10:00 PM</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="relative border border-white/10 bg-surface p-8 md:p-10">
              <div className="absolute left-0 top-0 h-[2px] w-24 bg-gold" />
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    data-testid="reservation-success"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  >
                    <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1} />
                    <h3 className="font-display mt-6 text-3xl tracking-tight text-white">Thank you!</h3>
                    <p className="mt-4 max-w-sm text-base font-light text-white/60">
                      We will contact you shortly to confirm availability.
                    </p>
                    <button
                      data-testid="reservation-again-button"
                      onClick={() => { setDone(false); setForm(EMPTY); }}
                      className="mt-8 border border-gold/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/10"
                    >
                      Make Another Request
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" data-testid="reservation-form" exit={{ opacity: 0 }} onSubmit={submit} className="grid gap-6 sm:grid-cols-2">
                    <Field label="Full Name">
                      <input data-testid="reservation-name-input" required value={form.full_name} onChange={set('full_name')} placeholder="Your full name" className={inputCls} />
                    </Field>
                    <Field label="Phone Number">
                      <input data-testid="reservation-phone-input" required type="tel" value={form.phone} onChange={set('phone')} placeholder="(305) 000-0000" className={inputCls} />
                    </Field>
                    <Field label="Email">
                      <input data-testid="reservation-email-input" required type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" className={inputCls} />
                    </Field>
                    <Field label="Vehicle Selection">
                      <select data-testid="reservation-vehicle-select" required value={form.vehicle} onChange={set('vehicle')} className={`${inputCls} appearance-none`}>
                        <option value="" disabled className="bg-surface">Select a vehicle</option>
                        {VEHICLES.map((v) => (
                          <option key={v.id} value={v.name} className="bg-surface">{v.name} — ${v.price}/day</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Pickup Date">
                      <input data-testid="reservation-pickup-date" required type="date" value={form.pickup_date} onChange={set('pickup_date')} className={inputCls} />
                    </Field>
                    <Field label="Return Date">
                      <input data-testid="reservation-return-date" required type="date" value={form.return_date} onChange={set('return_date')} className={inputCls} />
                    </Field>
                    <Field label="Pickup Location">
                      <input data-testid="reservation-pickup-location" required value={form.pickup_location} onChange={set('pickup_location')} placeholder="MIA, hotel, residence…" className={inputCls} />
                    </Field>
                    <Field label="Delivery Address (Optional)">
                      <input data-testid="reservation-delivery-address" value={form.delivery_address} onChange={set('delivery_address')} placeholder="Where should we bring it?" className={inputCls} />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Special Requests">
                        <textarea data-testid="reservation-special-requests" rows={4} value={form.special_requests} onChange={set('special_requests')} placeholder="Child seat, late arrival, occasion details…" className={`${inputCls} resize-none`} />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <button
                        data-testid="reservation-submit-button"
                        type="submit"
                        disabled={sending}
                        className="group flex w-full items-center justify-center gap-3 bg-gold py-5 text-xs font-semibold uppercase tracking-[0.25em] text-ink shadow-[0_0_25px_rgba(200,168,93,0.25)] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(200,168,93,0.4)] disabled:opacity-60"
                      >
                        {sending ? 'Sending…' : 'Request Reservation'}
                        {!sending && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Reserve;
